/**
 * PlanDetailsCard - Subscription details with win-back retention cancel flow.
 * Cancel flow: retention dialog -> reason capture -> confirmation -> cancel API.
 * Billing interval change: navigates to subscription screen (not inline dialog).
 * Tasks: DD-156, DD-1464, DD-1462
 */
import { useState } from 'react';
import { View, Text, Pressable, ActivityIndicator, Linking } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { TierBody } from '@dramadetector/ui';
import { GlassCard } from '../../glass';
import { useAlert } from '../../../contexts/AlertProvider';
import { trackEvent } from '@/lib/analytics/events';
import type { SubscriptionData } from '../../../hooks/useSubscription';
import { PRICING, BILLING_INTERVAL_SUFFIX } from '../../../../../lib/polar/constants';
import type { ProductKey } from '../../../../../lib/polar/constants';
import { useRouter } from 'expo-router';
import { useCancelSubscription } from '../../../hooks/useCancelSubscription';

type CancelReason = 'too_expensive' | 'not_using_enough' | 'missing_features' | 'found_alternative' | 'other';

interface PlanDetailsCardProps {
  subscription: SubscriptionData;
  planName: string;
  onCancelled: () => void;
  onOpenBillingPortal?: () => void;
}

/** Null-safe date formatting using system locale */
function displayDate(dateStr: string | null): string {
  if (!dateStr) return '---';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '---';
  }
}

export function PlanDetailsCard({
  subscription,
  planName,
  onCancelled,
  onOpenBillingPortal,
}: PlanDetailsCardProps) {
  const { t } = useTranslation('settings');
  const { showBillingModal } = useAlert();
  const router = useRouter();
  const { cancelSubscription, isCancelling } = useCancelSubscription();
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<CancelReason | null>(null);

  const isCancelPending = subscription.cancelAtPeriodEnd;
  const renewalDate = displayDate(subscription.currentPeriodEnd);

  // Calculate days until renewal for countdown display
  const daysUntilRenewal = subscription?.currentPeriodEnd
    ? Math.max(0, Math.ceil((new Date(subscription.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  // Show billing cycle change for active, non-canceling paid subscriptions
  const canChangeInterval =
    !isCancelPending &&
    !!subscription.billingInterval &&
    (subscription.tier === 'drama_plus' || subscription.tier === 'dramax');

  /** Step 1: Show retention dialog with alternatives */
  const showRetentionDialog = () => {
    setCancelError(null);

    trackEvent('subscription_retention_offered', {
      tier: subscription.tier,
      subscription_id: subscription.id ?? '',
    });

    showBillingModal({
      title: t('retention.title', { defaultValue: 'Before you go...' }),
      description: t('retention.message', { defaultValue: "We'd hate to see you leave! Here are some options:" }),
      buttons: [
        {
          text: t('retention.downgrade', { defaultValue: 'Downgrade instead' }),
          variant: 'primary',
          onPress: () => {
            trackEvent('subscription_retention_accepted', {
              tier: subscription.tier,
              subscription_id: subscription.id ?? '',
              action: 'downgrade',
            });
            router.push('/(tabs)/subscription');
          },
        },
        {
          text: t('retention.feedback', { defaultValue: 'I have feedback' }),
          variant: 'secondary',
          onPress: () => {
            trackEvent('subscription_retention_accepted', {
              tier: subscription.tier,
              subscription_id: subscription.id ?? '',
              action: 'feedback',
            });
            Linking.openURL('mailto:support@dramadetector.com?subject=Subscription Feedback');
          },
        },
        {
          text: t('retention.continue_canceling', { defaultValue: 'Continue canceling' }),
          variant: 'destructive',
          onPress: () => {
            trackEvent('subscription_retention_declined', {
              tier: subscription.tier,
              subscription_id: subscription.id ?? '',
            });
            showReasonDialog();
          },
        },
      ],
      testID: 'cancel-retention-step1',
    });
  };

  /** Step 2: Capture cancellation reason */
  const showReasonDialog = () => {
    showBillingModal({
      title: t('cancel_reason.title', { defaultValue: 'Help us improve' }),
      description: t('cancel_reason.message', { defaultValue: 'Why are you canceling? This helps us make DramaDetector better.' }),
      buttons: [
        {
          text: t('cancel_reason.too_expensive', { defaultValue: 'Too expensive' }),
          variant: 'secondary',
          onPress: () => onReasonSelected('too_expensive'),
        },
        {
          text: t('cancel_reason.not_using', { defaultValue: 'Not using it enough' }),
          variant: 'secondary',
          onPress: () => onReasonSelected('not_using_enough'),
        },
        {
          text: t('cancel_reason.missing_features', { defaultValue: 'Missing features I need' }),
          variant: 'secondary',
          onPress: () => onReasonSelected('missing_features'),
        },
        {
          text: t('cancel_reason.found_alternative', { defaultValue: 'Found an alternative' }),
          variant: 'secondary',
          onPress: () => onReasonSelected('found_alternative'),
        },
        {
          text: t('cancel_reason.other', { defaultValue: 'Other' }),
          variant: 'secondary',
          onPress: () => onReasonSelected('other'),
        },
      ],
      testID: 'cancel-reason',
    });
  };

  /** After reason is selected, show the final confirmation modal */
  const onReasonSelected = (reason: CancelReason) => {
    trackEvent('subscription_cancel_reason', {
      tier: subscription.tier,
      subscription_id: subscription.id ?? '',
      reason,
    });
    setCancelReason(reason);
    showConfirmationDialog(reason);
  };

  /** Step 3: Final confirmation modal */
  const showConfirmationDialog = (reason: CancelReason) => {
    const featureLoss = subscription.tier === 'dramax'
      ? [
          { text: t('plan_details.feature_loss_char', { defaultValue: '5,000 character limit' }) },
          { text: t('plan_details.feature_loss_analyses', { defaultValue: '200 daily analyses' }) },
          { text: t('plan_details.feature_loss_nc', { defaultValue: 'Full Narrative Clarity methodology' }) },
        ]
      : [
          { text: t('plan_details.feature_loss_char_plus', { defaultValue: '2,000 character limit' }) },
          { text: t('plan_details.feature_loss_analyses_plus', { defaultValue: '100 daily analyses' }) },
          { text: t('plan_details.feature_loss_spotlight', { defaultValue: 'Pattern Spotlight' }) },
        ];

    showBillingModal({
      title: t('plan_details.confirm_title', { defaultValue: 'Are you sure?' }),
      description: t('plan_details.confirm_message', {
        date: renewalDate,
        defaultValue: `You will keep access until ${renewalDate}, then revert to the Free plan.`,
      }),
      features: featureLoss,
      buttons: [
        {
          text: t('plan_details.keep_button', { defaultValue: 'Keep my plan' }),
          variant: 'primary',
          onPress: () => {
            setCancelReason(null);
          },
        },
        {
          text: t('plan_details.confirm_cancel', { defaultValue: 'Cancel subscription' }),
          variant: 'destructive',
          onPress: () => {
            setCancelReason(reason);
            performCancel();
          },
        },
      ],
      testID: 'cancel-confirmation',
    });
  };

  const handleCancelPress = () => {
    showRetentionDialog();
  };

  const performCancel = async () => {
    setCancelError(null);

    try {
      await cancelSubscription({
        subscriptionId: subscription.id ?? '',
        reason: cancelReason,
      });
      onCancelled();
    } catch (err) {
      if (err instanceof Error && err.message === 'Not authenticated') {
        setCancelError(t('auth.not_authenticated', { defaultValue: 'Not authenticated' }));
      } else if (err instanceof Error && err.message) {
        setCancelError(err.message || t('plan.cancel_error', { defaultValue: 'Failed to cancel subscription' }));
      } else {
        setCancelError(t('errors.network_retry', { defaultValue: 'Network error. Please try again.' }));
      }
    } finally {
      setCancelReason(null);
    }
  };

  return (
    <GlassCard>
      <View className="py-1">
        {/* Plan Name & Status */}
        <View className="flex-row items-center justify-between mb-3">
          <View className="flex-row items-center gap-2">
            <Text className="text-lg font-semibold text-gray-900">
              {planName}
            </Text>
            <View className={`px-2 py-0.5 rounded-full ${isCancelPending ? 'bg-amber-100' : 'bg-green-100'}`}>
              <Text className={`text-xs font-semibold ${isCancelPending ? 'text-amber-700' : 'text-green-700'}`}>
                {isCancelPending
                  ? t('plan_details.status_cancelling', { defaultValue: 'Cancelling' })
                  : t('plan_details.status_active', { defaultValue: 'Active' })
                }
              </Text>
            </View>
          </View>
        </View>

        {/* Renewal / Expiry Date */}
        <View className="mb-4">
          <TierBody size="sm" color="text-gray-600">
            {isCancelPending
              ? t('plan_details.active_until', { date: renewalDate, defaultValue: `Active until ${renewalDate}` })
              : t('plan_details.renews_on', { date: renewalDate, defaultValue: `Renews on ${renewalDate}` })
            }
          </TierBody>
          {/* Renewal countdown */}
          {daysUntilRenewal !== null && !isCancelPending && (
            <Text className="text-xs text-gray-400 mt-0.5">
              {daysUntilRenewal === 0
                ? t('plan_details.renews_today', { defaultValue: 'Renews today' })
                : daysUntilRenewal === 1
                  ? t('plan_details.renews_tomorrow', { defaultValue: 'Renews tomorrow' })
                  : t('plan_details.renews_in_days', {
                      count: daysUntilRenewal,
                      defaultValue: `Renews in ${daysUntilRenewal} days`,
                    })
              }
            </Text>
          )}
          {/* Billing interval and next renewal amount */}
          {subscription.billingInterval && (() => {
            const intervalLabel = subscription.billingInterval === 'month' ? 'monthly'
              : subscription.billingInterval === 'year' ? 'yearly'
              : subscription.billingInterval === 'week' ? 'weekly'
              : subscription.billingInterval === 'quarter' ? 'quarterly'
              : 'every 6 months';
            const productKey = `${subscription.tier}_${BILLING_INTERVAL_SUFFIX[subscription.billingInterval]}` as ProductKey;
            const pricing = PRICING[productKey];
            return (
              <View className="mt-1">
                <TierBody size="sm" color="text-gray-500">
                  {t('plan_details.billed_interval', {
                    interval: intervalLabel,
                    defaultValue: `Billed ${intervalLabel}`,
                  })}
                  {pricing && !isCancelPending && ` · ${t('plan_details.next_renewal_amount', {
                    amount: pricing.billedAs ?? pricing.displayPrice,
                    defaultValue: `Next: ${pricing.billedAs ?? pricing.displayPrice}`,
                  })}`}
                </TierBody>
              </View>
            );
          })()}
          {/* Manage payment method link */}
          {onOpenBillingPortal && !isCancelPending && (
            <Pressable onPress={onOpenBillingPortal} testID="manage-payment-method" className="mt-1">
              <Text className="text-xs text-blue-600 underline">
                {t('billing.manage_payment_method', { defaultValue: 'Manage payment method' })}
              </Text>
            </Pressable>
          )}
        </View>

        {/* Cancel Section */}
        {!isCancelPending && (
          <View>
            {cancelError && (
              <View className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
                <Text className="text-xs text-red-700">{cancelError}</Text>
              </View>
            )}

            <Pressable
              onPress={handleCancelPress}
              disabled={isCancelling}
              className="py-2.5 rounded-lg items-center bg-gray-100 active:bg-gray-200"
              accessibilityRole="button"
              accessibilityLabel="Cancel subscription"
              testID="cancel-subscription-button"
            >
              {isCancelling ? (
                <ActivityIndicator size="small" color="#6B7280" />
              ) : (
                <Text className="text-sm font-medium text-gray-700">
                  {t('plan_details.cancel_button', { defaultValue: 'Cancel Plan' })}
                </Text>
              )}
            </Pressable>
          </View>
        )}

        {/* Change Billing Cycle - navigates to subscription screen */}
        {canChangeInterval && (
          <View className="mt-3 pt-3 border-t border-gray-200">
            <Pressable
              onPress={() => router.push('/(tabs)/subscription')}
              disabled={isCancelling}
              className="py-2 items-center active:opacity-70"
              accessibilityRole="button"
              accessibilityLabel="Change billing cycle"
              testID="change-billing-cycle-button"
            >
              <Text className="text-sm text-gray-500">
                {t('plan_details.change_billing_cycle', { defaultValue: 'Change billing cycle' })}
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    </GlassCard>
  );
}
